import L from 'leaflet';
import GeoUtil from 'leaflet-geometryutil';
import { coordinatePairToMapBoundArray } from 'common/utils/helpers';
import { presetPolygonColors } from 'common/utils/constants';

const turf = require('@turf/turf');
const pointInPolygon = require('point-in-polygon');

export const getPolygonCoordinates = polygon => polygon.geometry.coordinates.map(coord => ([coord[1], coord[0]]));

export const confirmCenterPlacingByActivePolygon = (map, polygon) => {
  const bounds = map?.leafletElement?.getBounds();
  if (!bounds) return false;

  const boundArrayCoordinates = [coordinatePairToMapBoundArray(
    bounds._northEast.lat,
    bounds._northEast.lng,
    bounds._southWest.lat,
    bounds._southWest.lng
  )];

  const { geometry: { coordinates } } = polygon;
  const polygonCoordinates = [[...coordinates, coordinates[0]]];

  return !turf.intersect(turf.polygon(boundArrayCoordinates), turf.polygon(polygonCoordinates));
};

export const getCoordinatesFromLayer = (layer) => {
  const currentCoords = layer._latlngs[0];
  return currentCoords.map(coord => [coord.lng, coord.lat]);
};

export const getPolygonsByExistingPoint = (map, pointLocation) => {
  const polygons = [];

  map.eachLayer((layer) => {
    if (layer instanceof L.Polygon) {
      if (pointInPolygon([pointLocation.lng, pointLocation.lat], getCoordinatesFromLayer(layer))) {
        polygons.push(layer);
      }
    }
  });

  return polygons;
}

export const removeAllPolygonsFromMap = (map) => {
  map.eachLayer((layer) => {
    if (layer instanceof L.Polygon) {
      layer.unbindTooltip();
      layer.pm.remove();
    }
  });
}

export const getPolygonShape = (coordinates) => {
  const layer = L.polygon(coordinates.map(coord => ([coord[1], coord[0]])));
  return layer;
}

export const getDefaultPolygonColor = (exceptColor) => {
  const availableColors = presetPolygonColors.filter(color => color !== exceptColor);

  return availableColors[Math.floor(Math.random() * (presetPolygonColors.length - 1))];
};

export const getSelectedPolygon = (polygons, id) => {
  return polygons.find(polygon => polygon.deviceGroupId === id);
};

export const setActivePolygonColor = (map, deviceGroupId) => {
  map.leafletElement.eachLayer((layer) => {
    if (layer instanceof L.Polygon) {
      layer.setStyle({ fillColor: layer.deviceGroupId === deviceGroupId ? layer.options.color : 'transparent' });
    }
  });
};

export const updatePolygonAttributes = (map, deviceGroupId, attributes) => {
  map.leafletElement.eachLayer((layer) => {
    if (layer instanceof L.Polygon) {
      if (layer.deviceGroupId === deviceGroupId) {
        layer.setStyle({ 
          fillColor: attributes.fill, 
          color: attributes.fill
        });
        layer.name = attributes.name;
        layer.setTooltipContent(createPolygonLabelContent(attributes.name, attributes.fill));
      }
    }
  });
};

export const getPolygonClosestSegment = (latlng, polygon) => {
  const latlngs = [...polygon._latlngs[0], polygon._latlngs[0][0]];
  
  let segments = [];
  // get segments of polygon
  for (let i = 0; i < latlngs.length - 1; i++) {
    const pointToLineDistance = GeoUtil.distanceSegment(
      polygon._map,
      latlng,
      latlngs[i],
      latlngs[i + 1]
    );

    segments.push({
      index: i,
      pointToLineDistance,
      segment: [latlngs[i], latlngs[i + 1]]
    });
  }

  // sort segments by shortest distance
  segments.sort((a, b) =>
    a.pointToLineDistance < b.pointToLineDistance ? -1 : 1
  );

  // return first entry, which has shortest distance
  const shortestSegment = segments[0];
  return shortestSegment;
}

export const transformLeafletCoordsToGeojson = (layer) => {
  const layerCoords = getCoordinatesFromLayer(layer);
  if (layerCoords[0] !== layerCoords[layerCoords.length - 1]) {
    return [...layerCoords, layerCoords[0]];
  }
  return layerCoords;
};

export const createPolygonLabelContent = (content, color) => {
  const displayedText = content.length > 21 ? `${content.slice(0, 21)}...` : content;
  return `<span style="color: ${color}">${displayedText}</span>`
}

export const checkPolygonsNotEqual = (arr1 = [], arr2 = []) => {  
  if (arr1.length !== arr2.length) {
    return true;
  }

  return !arr1.every((val, index) => val.deviceGroupId === arr2[index].deviceGroupId);
}